client import { EditorView, keymap } from "@codemirror/view";
client import { EditorState } from "@codemirror/state";
client import extensions, { update } from "app/util/codemirror";
client import { formatCode } from "app/util/format";
client import { getLspClient, type LspClient } from "app/util/lsp/client";
client import { markoLsp } from "app/util/lsp/codemirror";
import { faPaintbrush } from "@fortawesome/free-solid-svg-icons";
import type { File } from "app/util/workspace";
import styles from "./editor.styles.module.scss";
export interface Input {
  files: File[];
  filesChange?: (files: File[]) => void;
  tab: number;
  tabChange?: (tab: number) => void;
}

static const ext = (path: string) => path.slice(path.lastIndexOf(".") + 1);

let/files:=input.files
let/selected:=input.tab
let/file=files[selected]
  ,valueChange(newValue) {
    files = files.toSpliced(selected, 1, newValue);
  }

// Bridges the format button (markup) to the editor's lifecycle; set on mount.
let/doFormat=() => {}

tabs files:=files selected:=selected
div class=styles.editorWrap
  div.shiki/$editor id=styles.editor
  button
    ,type="button"
    ,title="Format (Shift+Alt+F)"
    ,class=styles.action
    ,onClick() {
      doFormat();
    }
    fa-icon=faPaintbrush
    html-script --
      ${
        `if(navigator.platform.startsWith("Mac"))document.currentScript.parentElement.title="Format (⇧⌘F)"`
      };

lifecycle<{
  editor?: EditorView;
  client?: LspClient;
  listener: ReturnType<typeof EditorView.updateListener.of>;
  currentPath?: string;
  change?: boolean;
  // A pending go-to-definition jump into another tab: applied once that tab's
  // editor state is live (see onUpdate).
  pendingNav?: { path: string; position: { line: number; character: number } };
  set(content: string): void;
  format(): void;
  openLocation(
    path: string,
    position: { line: number; character: number },
  ): void;
  focusPosition(position: { line: number; character: number }): void;
  stateFor(f: File | undefined): EditorState;
}>
  ,set(content) {
    this.change = content !== file.content;
    if (this.change) {
      file = { ...file, content };
    }
  }
  ,format() {
    const view = this.editor;
    if (!view || !file) return;
    const content = view.state.doc.toString();
    const cursorOffset = view.state.selection.main.head;
    const pending = formatCode(content, cursorOffset, ext(file.path));
    if (!pending) return;
    void pending
      .then((result) => {
        // Skip if the doc changed while formatting, or is already formatted.
        if (
          result.formatted === content ||
          view.state.doc.toString() !== content
        ) {
          return;
        }
        view.dispatch({
          changes: { from: 0, to: content.length, insert: result.formatted },
          selection: {
            anchor: Math.min(result.cursorOffset, result.formatted.length),
          },
        });
      })
      .catch(() => {
        // Ignore formatting errors (e.g. the file currently has a syntax error).
      });
  }
  ,openLocation(targetPath, position) {
    const index = files.findIndex((f) => f.path === targetPath);
    // A target outside the open playground files (eg a lib `.d.ts`) has no tab
    // to open, so leave the editor where it is.
    if (index < 0) return;
    if (index === selected) {
      this.focusPosition(position);
    } else {
      this.pendingNav = { path: targetPath, position };
      selected = index;
    }
  }
  ,focusPosition(position) {
    const view = this.editor;
    if (!view) return;
    const { doc } = view.state;
    const line = doc.line(Math.min(Math.max(position.line + 1, 1), doc.lines));
    const anchor = Math.min(line.from + position.character, line.to);
    view.dispatch({ selection: { anchor }, scrollIntoView: true });
    view.focus();
  }
  ,stateFor(f) {
    return EditorState.create({
      doc: f?.content,
      extensions: [
        this.listener,
        keymap.of([
          {
            // Alt+letter is a dead key on macOS, so use Cmd there instead.
            key: "Shift-Alt-f",
            mac: "Shift-Cmd-f",
            run: () => {
              this.format();
              return true;
            },
          },
        ]),
        ...extensions,
        ...(f && this.client
          ? markoLsp(this.client, f.path, (p, position) =>
              this.openLocation(p, position),
            )
          : []),
      ],
    });
  }
  ,onMount() {
    this.change = false;
    try {
      // The language server is a best-effort enhancement; if it fails to start
      // (eg the worker can't be created) the editor still works without it.
      this.client = getLspClient();
      this.client.syncFiles(files);
    } catch {
      this.client = undefined;
    }
    this.listener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        this.set(update.state.doc.toString());
      }
    });
    this.currentPath = file?.path;
    this.editor = new EditorView({
      state: this.stateFor(file),
      parent: $editor(),
    });
    doFormat = () => this.format();
    if (file) {
      update(this.editor, file.content, ext(file.path));
    }
  }
  ,onUpdate() {
    // Keep the language server's view of every file current so cross-file
    // features (imported components, shared types) resolve.
    this.client?.syncFiles(files);
    if (this.change) {
      this.change = false;
    } else if (file) {
      if (file.path !== this.currentPath) {
        // Switching tabs: give the new file its own editor state so undo
        // history and selection don't bleed across files.
        this.currentPath = file.path;
        this.editor!.setState(this.stateFor(file));
      }
      update(this.editor!, file.content, ext(file.path));
    }
    // A go-to-definition jump into this tab: now that its editor state is live,
    // place the cursor at the target.
    if (this.pendingNav && file && this.pendingNav.path === file.path) {
      this.focusPosition(this.pendingNav.position);
      this.pendingNav = undefined;
    }
  }
  ,onDestroy() {
    this.editor!.destroy();
  }
